import { useTranslation } from 'react-i18next'
import { Loader2, ChevronLeft, ChevronRight } from 'lucide-react'
import PerPageSelector from './Toolbar/PerPageSelector'

interface PaginationProps {
  page: number
  pages: number
  count: number
  from: number
  to: number
  perPage: number
  onPageChange: (page: number, direction: 'prev' | 'next') => void
  onPerPageChange: (perPage: number) => void
  loadingPrev: boolean
  loadingNext: boolean
  loadingPerPage: boolean
}

export default function Pagination({
  page,
  pages,
  count,
  from,
  to,
  perPage,
  onPageChange,
  onPerPageChange,
  loadingPrev,
  loadingNext,
  loadingPerPage
}: PaginationProps) {
  const { t } = useTranslation()

  return (
    <div className="bg-base-200 rounded-lg p-2 sm:p-4">
      {/* Mobile: Stacked Layout */}
      <div className="flex flex-col gap-3 sm:hidden">
        {/* Row 1: Pagination Controls */}
        <div className="flex justify-between items-center">
          <div className="join">
            <button
              className="join-item btn btn-sm"
              disabled={page === 1 || loadingPrev}
              onClick={() => onPageChange(page - 1, 'prev')}
            >
              {loadingPrev ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronLeft className="w-4 h-4" />}
            </button>

            <div className="join-item flex items-center px-3 bg-base-200 text-xs">
              {page}/{pages}
            </div>

            <button
              className="join-item btn btn-sm"
              disabled={page === pages || loadingNext}
              onClick={() => onPageChange(page + 1, 'next')}
            >
              {loadingNext ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          </div>
          <PerPageSelector
            perPage={perPage}
            onPerPageChange={onPerPageChange}
            isLoading={loadingPerPage}
          />
        </div>

        {/* Row 2: Showing info */}
        <div className="text-xs text-center text-base-content/70">
          {from}-{to} {t('fileExplorer.pagination.of')} {count}
        </div>
      </div>

      {/* Desktop: Single Row Layout */}
      <div className="hidden sm:flex justify-between items-center">
        <div className="flex items-center gap-3">
          <span className="text-sm">
            {t('fileExplorer.pagination.showing')} {from} {t('fileExplorer.pagination.to')} {to} {t('fileExplorer.pagination.of')} {count} {t('fileExplorer.pagination.items')}
          </span>
          <PerPageSelector
            perPage={perPage}
            onPerPageChange={onPerPageChange}
            isLoading={loadingPerPage}
          />
        </div>

        <div className="join">
          <button
            className="join-item btn btn-sm"
            disabled={page === 1 || loadingPrev}
            onClick={() => onPageChange(page - 1, 'prev')}
            data-testid="pagination-prev"
          >
            {loadingPrev ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronLeft className="w-4 h-4" />}
          </button>

          <div className="join-item flex items-center px-4 bg-base-200 text-sm" data-testid="pagination-info">
            {t('fileExplorer.pagination.page')} {page} {t('fileExplorer.pagination.of')} {pages}
          </div>

          <button
            className="join-item btn btn-sm"
            disabled={page === pages || loadingNext}
            onClick={() => onPageChange(page + 1, 'next')}
            data-testid="pagination-next"
          >
            {loadingNext ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  )
}
